import React, { useEffect, useState } from "react"
import ChartContainer from "components/Shared/ChartContainer"
import { MetricsBySegments } from "components/CustomerBehavior/CustomerProfiling/MetricsBySegments"
import { useFetchData } from "components/Shared/Hooks/react-query/useFetchData"
import {
	CustomerProfilingTable,
	getCustomerProfilingTableMetric,
	CustomerProfilingTimeline,
	getCustomerProfilingTimelineMetric,
} from "components/CustomerBehavior/CustomerProfiling/service"
import { FiltersType } from "components/Shared/GlobalFilter/context/interfaces"
import AsyncMessageResponseHandler from "components/Shared/AsyncMessageResponseHandler"
import { useIntl } from "react-intl"
import { AGE, GENDER } from "components/Shared/GlobalFilter/constants"
import { dataTimelineDefault, placeholderText } from "../../Shared/utils"
import Timeline from "components/Shared/Timeline"
import { Select } from "components/Shared/Select"
import {
	WEEKLY_INTERVAL,
	formatAge,
	formatGender,
} from "components/Shared/Timeline/constants"
import {
	options,
	TRANSACTIONS,
} from "components/CustomerBehavior/CustomerProfiling/constants"
import Tooltip from "components/CustomerBehavior/CustomerProfiling/Shared/Tooltip"
import { DateInterval } from "components/Shared/PhronesisCalendar/PhronesisCalendar.utils"

interface DemographicSegmentationProps {
	period: DateInterval
	filters: FiltersType
}

const DemographicSegmentation: React.FC<DemographicSegmentationProps> = ({
	period,
	filters,
}) => {
	const intl = useIntl()
	const [localPeriod, setLocalPeriod] = useState<DateInterval>(period)
	const [intervalAge, setIntervalAge] = useState<string>(WEEKLY_INTERVAL)
	const [intervalGender, setIntervalGender] = useState<string>(WEEKLY_INTERVAL)
	const [metricAge, setMetricAge] = useState<string>(TRANSACTIONS)
	const [metricGender, setMetricGender] = useState<string>(TRANSACTIONS)

	useEffect(() => {
		setIntervalAge(WEEKLY_INTERVAL)
		setIntervalGender(WEEKLY_INTERVAL)
		setLocalPeriod(period)
	}, [period])

	const {
		data: dataAge = [],
		isFetching: isFetchingAge,
		error: errorDataChartAge,
		isError: isErrorInDataChartAge,
		refetch: refetchDataChartAge,
	} = useFetchData<Array<CustomerProfilingTable>>(
		"retrieveCustomerProfilingTable",
		getCustomerProfilingTableMetric,
		{ period, filters, segment: AGE }
	)
	const {
		data: dataGender = [],
		isFetching: isFetchingGender,
		error: errorDataChartGender,
		isError: isErrorInDataChartGender,
		refetch: refetchDataChartGender,
	} = useFetchData<Array<CustomerProfilingTable>>(
		"retrieveCustomerProfilingTable",
		getCustomerProfilingTableMetric,
		{
			period,
			filters,
			segment: GENDER,
		}
	)

	const {
		data: dataAgeTimeline = dataTimelineDefault,
		isFetching: isFetchingAgeTimeline,
		error: errorDataAgeTimeline,
		isError: isErrorInDataAgeTimeline,
		refetch: refetchDataAgeTimeline,
	} = useFetchData<CustomerProfilingTimeline>(
		"retrieveCustomerProfilingTimeline",
		getCustomerProfilingTimelineMetric,
		{
			period: localPeriod,
			metric: metricAge,
			filters,
			interval: intervalAge,
			segment: AGE,
		},
		{
			enabled: true,
			retry: false,
		}
	)

	const {
		data: dataGenderTimeline = dataTimelineDefault,
		isFetching: isFetchingGenderTimeline,
		error: errorDataGenderTimeline,
		isError: isErrorInDataGenderTimeline,
		refetch: refetchDataGenderTimeline,
	} = useFetchData<CustomerProfilingTimeline>(
		"retrieveCustomerProfilingTimeline",
		getCustomerProfilingTimelineMetric,
		{
			period: localPeriod,
			metric: metricGender,
			filters,
			interval: intervalGender,
			segment: GENDER,
		},
		{
			enabled: true,
			retry: false,
		}
	)

	const validateIsFetchingHasData = () => {
		const hasData = [
			dataAge.length,
			dataGender.length,
			dataAgeTimeline.length,
			dataGenderTimeline.length,
		].reduce((prev, curr) => prev && curr)
		const isLoading = [
			isFetchingAge,
			isFetchingGender,
			isFetchingAgeTimeline,
			isFetchingGenderTimeline,
		].reduce((prev, curr) => prev || curr)
		return {
			hasData,
			isLoading,
		}
	}

	const validateDataChartError = () => {
		const isError = [
			isErrorInDataChartAge,
			isErrorInDataChartGender,
			isErrorInDataAgeTimeline,
			isErrorInDataGenderTimeline,
		].reduce((prev, curr) => prev && curr)
		const error = [
			errorDataChartAge,
			errorDataGenderTimeline,
			errorDataAgeTimeline,
			errorDataChartGender,
		].reduce((prev, curr) => prev || curr)
		return {
			isError,
			error,
		}
	}

	return (
		<AsyncMessageResponseHandler
			hasData={validateIsFetchingHasData().hasData}
			isLoading={validateIsFetchingHasData().isLoading}
			isError={validateDataChartError().isError}
			error={validateDataChartError().error}
			retry={() => {
				refetchDataChartAge()
				refetchDataChartGender()
				refetchDataAgeTimeline()
				refetchDataGenderTimeline()
			}}
			className="benchmark-error-container"
		>
			<div className="chart-container">
				<ChartContainer
					title={{
						id:
							"phronesis.customerbehavior.application.customerProfiling.metricsBySegments.title.age",
					}}
					body={{
						id:
							"phronesis.customerbehavior.application.customerProfiling.metricsBySegments.description.segment",
						values: {
							segment: intl.formatMessage({
								id:
									"phronesis.customerbehavior.application.customerProfiling.metricsBySegments.description.segment.age",
							}),
						},
					}}
					isLoading={isFetchingAge}
				>
					<MetricsBySegments
						data={dataAge}
						metricSegment={AGE}
						setMetricSegment={() => {}}
						segmentSelected={AGE}
						isIndividual={true}
					/>
				</ChartContainer>
			</div>

			<div className="chart-container">
				<Timeline
					data={dataAgeTimeline}
					format={formatAge}
					title={{ id: placeholderText.ageChart }}
					subtitle={{ id: placeholderText.ageChartDescription }}
					isLoading={isFetchingAgeTimeline}
					tooltip={<Tooltip />}
					interval={intervalAge}
					setInterval={setIntervalAge}
					body={
						<div className="select-segment">
							<Select
								id="select-segment"
								options={options(intl)}
								label={intl.formatMessage({
									id:
										"phronesis.customerbehavior.application.customerProfiling.timeline.select.title",
								})}
								value={metricAge}
								onChange={(option) => setMetricAge(option.code)}
							/>
						</div>
					}
				/>
			</div>

			<div className="chart-container">
				<ChartContainer
					title={{
						id:
							"phronesis.customerbehavior.application.customerProfiling.metricsBySegments.title.gender",
					}}
					body={{
						id:
							"phronesis.customerbehavior.application.customerProfiling.metricsBySegments.description.segment",
						values: {
							segment: intl.formatMessage({
								id:
									"phronesis.customerbehavior.application.customerProfiling.metricsBySegments.description.segment.gender",
							}),
						},
					}}
					isLoading={isFetchingGender}
				>
					<MetricsBySegments
						data={dataGender}
						metricSegment={GENDER}
						setMetricSegment={() => {}}
						segmentSelected={GENDER}
						isIndividual={true}
					/>
				</ChartContainer>
			</div>

			<div className="chart-container">
				<Timeline
					data={dataGenderTimeline}
					format={formatGender}
					title={{ id: placeholderText.genderChart }}
					subtitle={{ id: placeholderText.genderChartDescription }}
					isLoading={isFetchingGenderTimeline}
					tooltip={<Tooltip />}
					interval={intervalGender}
					setInterval={setIntervalGender}
					body={
						<div className="select-segment">
							<Select
								id="select-segment"
								options={options(intl)}
								label={intl.formatMessage({
									id:
										"phronesis.customerbehavior.application.customerProfiling.timeline.select.title",
								})}
								value={metricGender}
								onChange={(option) => setMetricGender(option.code)}
							/>
						</div>
					}
				/>
			</div>
		</AsyncMessageResponseHandler>
	)
}

export default DemographicSegmentation
